import React, { useState } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Calendar, Target, Zap, AlertTriangle, TrendingUp, Clock, CheckCircle2 } from "lucide-react";

const HEALTH_META = {
  ahead:    { label: "Ahead of schedule", tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: TrendingUp },
  on_track: { label: "On track",           tone: "bg-emerald-50 text-emerald-700 border-emerald-200", icon: CheckCircle2 },
  at_risk:  { label: "At risk",            tone: "bg-yellow-50 text-yellow-800 border-yellow-200",  icon: AlertTriangle },
  delayed:  { label: "Delayed",            tone: "bg-red-50 text-red-700 border-red-200",           icon: AlertTriangle },
  unknown:  { label: "No target set",      tone: "bg-[#F1F3F5] text-[#6C757D] border-[#E9ECEF]",    icon: Target },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}

/**
 * Phase-1 spec § 10 — One-glance dealer progress header. Shows Production %,
 * Target Install, Estimated Ready, business days remaining, and Schedule
 * Health badge. Admin/Agency admin/Owner-agent can set/update the target.
 */
export default function DealerProgressHeader({ rec, canEditTarget, onChanged }) {
  const [dlgOpen, setDlgOpen] = useState(false);
  const [dateVal, setDateVal] = useState(rec?.target_install_date ? rec.target_install_date.slice(0, 10) : "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const health = HEALTH_META[rec?.schedule_health || "unknown"] || HEALTH_META.unknown;
  const HealthIcon = health.icon;
  const bdr = rec?.business_days_remaining;

  const save = async () => {
    if (!dateVal) {
      toast.error("Pick a target date");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/onboarding/${rec.id}/target-install-date`, {
        target_install_date: dateVal,
        reason: reason || null,
      });
      if (data.expedited) {
        toast.warning(`EXPEDITED — target is ${data.business_days_remaining} business day${data.business_days_remaining === 1 ? "" : "s"} away. Standard lead time is 3–5 days.`);
      } else {
        toast.success("Target install date saved");
      }
      setDlgOpen(false);
      setReason("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-sm border-2 border-[#0B132B]/10 bg-gradient-to-br from-white to-[#F8F9FA] p-5 mb-4"
      data-testid="dealer-progress-header"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        {/* Left — Production % */}
        <div className="min-w-[140px]">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Production</div>
          <div className="flex items-baseline gap-1 mt-1">
            <span className="text-4xl font-black text-[#0B132B] tracking-tight" data-testid="header-production-pct">
              {rec?.production_pct ?? 0}
            </span>
            <span className="text-lg font-bold text-[#6C757D]">%</span>
          </div>
          <div className="w-full h-1.5 bg-[#E9ECEF] rounded-full mt-2 overflow-hidden">
            <div
              className="h-full bg-[#D4AF37] rounded-full transition-all"
              style={{ width: `${rec?.production_pct ?? 0}%` }}
            />
          </div>
        </div>

        {/* Middle — Target vs ETA */}
        <div className="flex-1 grid grid-cols-2 gap-4 min-w-[280px]">
          <div>
            <div className="flex items-center gap-1.5">
              <Target size={11} className="text-[#D4AF37]" />
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Target install</div>
            </div>
            <div className="text-lg font-bold text-[#0B132B] mt-1" data-testid="header-target-date">
              {fmtDate(rec?.target_install_date)}
            </div>
            {rec?.expedited && (
              <span className="inline-flex items-center gap-1 mt-1 text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 rounded-sm font-bold">
                <Zap size={10} /> Expedited
              </span>
            )}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <Clock size={11} className="text-[#0B132B]" />
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">Estimated ready</div>
            </div>
            <div className="text-lg font-bold text-[#0B132B] mt-1" data-testid="header-eta-date">
              {rec?.estimated_ready_label || fmtDate(rec?.estimated_ready_date)}
            </div>
          </div>
        </div>

        {/* Right — Schedule Health + Days Remaining */}
        <div className="flex flex-col items-end gap-2 min-w-[160px]">
          <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-xs uppercase tracking-[0.18em] font-bold ${health.tone}`}
               data-testid="header-schedule-health">
            <HealthIcon size={13} /> {health.label}
          </div>
          {bdr !== null && bdr !== undefined && rec?.target_install_date && (
            <div className="text-xs text-[#6C757D]" data-testid="header-business-days-remaining">
              {bdr < 0
                ? <span className="text-red-700 font-bold">{Math.abs(bdr)} business day{Math.abs(bdr) === 1 ? "" : "s"} overdue</span>
                : bdr === 0
                  ? <span className="text-yellow-800 font-bold">Due today</span>
                  : <span><b>{bdr}</b> business day{bdr === 1 ? "" : "s"} remaining</span>}
            </div>
          )}
          {canEditTarget && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDlgOpen(true)}
              className="h-8 rounded-sm text-xs border-[#0B132B]/20"
              data-testid="btn-set-target-install"
            >
              <Calendar size={12} className="mr-1" /> {rec?.target_install_date ? "Change target" : "Set target"}
            </Button>
          )}
        </div>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="target-install-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Target install date</DialogTitle>
            <DialogDescription className="text-xs text-[#6C757D]">
              The date the agent would like this dealer installed. AWG will calculate an Estimated Ready date based on remaining work and compare against this target.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Target date</Label>
              <Input
                type="date"
                value={dateVal}
                onChange={(e) => setDateVal(e.target.value)}
                className="mt-2 h-10 rounded-sm"
                data-testid="target-install-date-input"
              />
            </div>
            {rec?.target_install_date && (
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Reason for change (optional)</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is the date changing?"
                  className="mt-2 h-10 rounded-sm"
                  data-testid="target-install-reason-input"
                />
              </div>
            )}
            <div className="text-[11px] text-[#6C757D] italic bg-[#F8F9FA] p-2 rounded-sm">
              Aggressive dates (&lt; 3 business days) are allowed but the record will be flagged as <b>EXPEDITED</b> for Support.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !dateVal}
              className="bg-[#0B132B] hover:bg-[#1C2541] text-white"
              data-testid="btn-save-target-install"
            >
              {saving ? "Saving…" : "Save target"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
